import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext();

// Role constants
export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  CLUB_ADMIN: 'club_admin'
};

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('padel-ladder-token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setAdmin(response.data);
        } catch (error) {
          console.error('Auth init failed:', error);
          localStorage.removeItem('padel-ladder-token');
          setToken(null);
          setAdmin(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [token]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, admin: adminData } = response.data;
    localStorage.setItem('padel-ladder-token', access_token);
    setToken(access_token);
    setAdmin(adminData);
    return adminData;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('padel-ladder-token');
    setToken(null);
    setAdmin(null);
  }, []);

  const getAuthHeaders = useCallback(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);

  // Role checks
  const isSuperAdmin = useMemo(() => {
    return admin?.role === ROLES.SUPER_ADMIN;
  }, [admin]);

  const isClubAdmin = useMemo(() => {
    return admin?.role === ROLES.CLUB_ADMIN;
  }, [admin]);

  const value = useMemo(() => ({
    admin,
    token,
    loading,
    login,
    logout,
    getAuthHeaders,
    isAuthenticated: !!admin,
    isSuperAdmin,
    isClubAdmin,
    role: admin?.role || null
  }), [admin, token, loading, login, logout, getAuthHeaders, isSuperAdmin, isClubAdmin]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
